import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';
import ReactToPrint from 'react-to-print';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { Popconfirm, Input, Tag, Select, DatePicker, Table, Switch, Button, Tooltip, notification, Avatar, Modal, Tabs, Segmented, InputNumber } from 'antd';
import { CheckCircleOutlined, PrinterOutlined, SyncOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { Comment } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import {
  businessAtom,
  timezoneAtom,
  profileAtom
} from '../../../atoms/Atoms';
import { dateRangePresets } from '../../utils/utils';
import api from '../../../api/api';
import envConfig from '../../../envConfig';
import { renderHour, renderShortDate, renderShortDateHour, renderShortDateTime, thousand, thousandv2, toCurrency } from '../../utils/functions';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';

function TeamTimesheets() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const timezone = useRecoilValue(timezoneAtom);
  const business = useRecoilValue(businessAtom);
  const [selectedDates, setSelectedDates] = useState({
    start: moment().startOf('week').format('YYYY-MM-DD'),
    end: moment().endOf('week').format('YYYY-MM-DD')
  });
  const [loadingTimeSheet, setLoadingTimeSheet] = useState(false);
  const [loadingClockedIn, setLoadingClockedIn] = useState(false);
  const [timesheetData, setTimesheetData] = useState([]);
  const [timesheetDataGrouped, setTimesheetDataGrouped] = useState([]);
  const [timesheetDataIndividual, setTimesheetDataIndividual] = useState([]);
  const [selectedOption, setSelectedOption] = useState(t('default'));
  const [clockedInData, setClockedInData] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [showPayroll, setShowPayroll] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(envConfig.TimesheetHourlyRate?.length > 0 ? parseFloat(envConfig.TimesheetHourlyRate) : 0);
  const [deductionPercentage, setDeductionPercentage] =
    useState(envConfig.TimesheetDeductionRate?.length > 0 ? parseFloat(envConfig.TimesheetDeductionRate) : 0);
  const [selectedTimeSheet, setSelectedTimeSheet] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updatingTimesheet, setUpdatingTimesheet] = useState(false);
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [selectedType, setSelectedType] = useState('');

  const [creatingNewEntry, setCreatingNewEntry] = useState(false);
  const [createNewEntry, setCreateNewEntry] = useState(false);
  const [newEntryData, setNewEntryData] = useState(null);

  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const userProfile = useRecoilValue(profileAtom);
  const isAdmin =
    userProfile.contents &&
    userProfile?.contents?.roles?.includes('SuperAdmin');
  const isCustomerService =
    isAdmin ||
    (userProfile?.contents &&
      (userProfile?.contents?.roles?.includes('CustomerSupportAdmin') ||
        userProfile?.contents?.roles?.includes('CustomerSupport')));
  const isStoreManager =
        isAdmin ||
        isCustomerService ||
        (userProfile?.contents &&
          (userProfile?.contents?.roles?.includes('StoreManager') ||
          userProfile?.contents?.roles?.includes('StoreAdmin')));

  const getTypes = function () {
    setLoadingTypes(true);
    api
      .get('timesheet/types')
      .then((response) => {
        setTypes(response.data.data.results);
        setLoadingTypes(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingTypes(false);
      });
  };

  const getStaff = function () {
    setLoadingStaff(true);
    api
      .get(`businesses/${business?.id}/team`)
      .then((response) => {
        const activeStafff = _.sortBy(_.filter(response.data.data.results, (s) => s.isActive), (s2) => s2.completeName);
        const nonActiveStafff = _.sortBy(_.filter(response.data.data.results, (s) => !s.isActive), (s2) => s2.completeName);
        setStaff([...activeStafff, ...nonActiveStafff]);
        setLoadingStaff(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingStaff(false);
      });
  };

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getTimeSheetData() {
    if (!loadingTimeSheet && business) {
      setLoadingTimeSheet(true);
      const startTime = moment(selectedDates.start).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
      const endTime = moment(selectedDates.end).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').add(1, 'day').add(-1, 'minute')
        .format('YYYY-MM-DDTHH:mm:00.000');
      let url = `timesheet?startDate=${startTime}&endDate=${endTime}&businessId=${business.id}`;
      if (selectedEmployeeId?.length > 0) {
        url += `&employeeId=${selectedEmployeeId}`;
      }
      if (selectedType?.length > 0) {
        url += `&timeSheetTypeId=${selectedType}`;
      }
      api.get(url)
        .then((response) => {
          if (response.data.success) {
            const temp = _.map(response.data.data.results, (ts) => ({
              ...ts,
              id: `${ts.user.id}-${ts.date}`,
              userId: ts.user.id,
              lunch: 0,
              lunchHour: 0,
              lunchMinute: 0
            }));
            setTimesheetData(temp);
            const individual = [];
            for (let i = 0; i < temp?.length; i++) {
              for (let j = 0; j < temp[i].timesheet?.length; j++) {
                individual.push({
                  date: temp[i].date,
                  businessId: temp[i].businessId,
                  businessName: temp[i].businessName,
                  totalHours: temp[i].totalHours,
                  totalMinutes: temp[i].totalMinutes,
                  user: temp[i].user,
                  id: temp[i].timesheet[j].id,
                  startTime: temp[i].timesheet[j].startTime,
                  endTime: temp[i].timesheet[j].endTime,
                  hours: temp[i].timesheet[j].hours,
                  minutes: temp[i].timesheet[j].minutes,
                  lunch: 0,
                  lunchHour: 0,
                  lunchMinute: 0,
                  comment: temp[i].timesheet[j].comment,
                  employeeTimeSheetTypeId: temp[i].timesheet[j].employeeTimeSheetTypeId,
                  type: temp[i].timesheet[j].type
                });
              }
            }
            const tempGrouped = _.groupBy(temp, (sheet) => sheet.userId);
            const grouped = _.map(tempGrouped, (userSheets) => {
              const tempSheet = {
                ...userSheets[0],
                timesheet: []
              };
              for (let i = 0; i < userSheets?.length; i++) {
                if (i !== 0) {
                  tempSheet.totalHours += userSheets[i].totalHours;
                  tempSheet.totalMinutes += userSheets[i].totalMinutes;
                }
                for (let j = 0; j < userSheets[i].timesheet?.length; j++) {
                  tempSheet.timesheet.push(userSheets[i].timesheet[j]);
                }
              }
              return tempSheet;
            });
            setTimesheetDataGrouped(grouped);
            setTimesheetDataIndividual(individual);
          } else {
            showMessage(response.data.message);
          }
        })
        .catch((error) => {
          console.error(error);
          showMessage(error.message);
        })
        .finally(() => setLoadingTimeSheet(false));
    }
  }

  function updateTimesheet() {
    setUpdatingTimesheet(true);
    const startTime = dayjs(selectedTimeSheet.startTime).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
    const endTime = selectedTimeSheet.endTime ? dayjs(selectedTimeSheet.endTime).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null;
    api.put('timesheet', {
      TimeSheetId: selectedTimeSheet.id,
      StartTime: startTime,
      EndTime: endTime,
      EmployeeTimeSheetTypeId: selectedTimeSheet.employeeTimeSheetTypeId,
      Comment: selectedTimeSheet.comment
    })
      .then((response) => {
        if (response.data.success) {
          setSelectedTimeSheet(null);
          setShowModal(false);
          getTimeSheetData();
        } else {
          showMessage(response.data.message);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
      })
      .finally(() => {
        setUpdatingTimesheet(false);
      });
  }

  function deleteTimesheet() {
    setUpdatingTimesheet(true);
    api.delete(`timesheet/remove/${selectedTimeSheet.id}`)
      .then((response) => {
        if (response.data.success) {
          setSelectedTimeSheet(null);
          setShowModal(false);
          getTimeSheetData();
        } else {
          showMessage(response.data.message);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
      })
      .finally(() => {
        setUpdatingTimesheet(false);
      });
  }

  function createNewTimeSheetEntry() {
    setCreatingNewEntry(true);
    const startTime = dayjs(newEntryData.startTime).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000');
    const endTime = newEntryData.endTime ? dayjs(newEntryData.endTime).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null;
    api.post('timesheet', {
      EmployeeId: newEntryData.userId,
      StartTime: startTime,
      EndTime: endTime,
      BusinessId: business?.id,
      EmployeeTimeSheetTypeId: newEntryData.employeeTimeSheetTypeId,
      Comment: newEntryData.comment
    })
      .then((response) => {
        if (response.data.success) {
          setCreateNewEntry(false);
          getTimeSheetData();
        } else {
          showMessage(response.data.message);
        }
      })
      .catch((error) => {
        console.error(error);
        showMessage(error.message);
      })
      .finally(() => {
        setCreatingNewEntry(false);
      });
  }

  function getClockedIn() {
    if (!loadingClockedIn && business) {
      setLoadingClockedIn(true);
      api.get(`timesheet/clockedin?businessId=${business?.id}`)
        .then((response) => {
          if (response.data.success) {
            setClockedInData(response.data.data.results);
          } else {
            showMessage(response.data.message);
          }
        })
        .catch((error) => {
          console.error(error);
          showMessage(error.message);
        })
        .finally(() => setLoadingClockedIn(false));
    }
  }

  useEffect(() => {
    getTypes();
    setSelectedType('');
    setSelectedOption(t('default'));
    setSelectedDates({
      start: moment().startOf('week').format('YYYY-MM-DD'),
      end: moment().endOf('week').format('YYYY-MM-DD')
    });
  }, []);

  useEffect(() => {
    if (business) {
      getTimeSheetData();
    }
  }, [business, selectedDates]);

  useEffect(() => {
    if (business) {
      getStaff();
      getClockedIn();
    }
  }, [business]);

  useEffect(() => {
    if (!showModal) {
      setSelectedTimeSheet(null);
    }
  }, [showModal]);

  useEffect(() => {
    if (!createNewEntry) {
      setNewEntryData(null);
    } else {
      setNewEntryData({
        userId: '',
        startTime: null,
        endTime: null,
        employeeTimeSheetTypeId: types[0]?.id
      });
    }
  }, [createNewEntry]);

  useEffect(() => {
    const tempDefault = [...timesheetData];
    const tempGrouped = [...timesheetDataGrouped];
    const tempIndividual = [...timesheetDataIndividual];
    for (let i = 0; i < tempDefault.length; i++) {
      tempDefault[i].lunch = 0;
      tempDefault[i].lunchHour = 0;
      tempDefault[i].lunchMinute = 0;
    }
    for (let i = 0; i < tempGrouped.length; i++) {
      tempGrouped[i].lunch = 0;
      tempGrouped[i].lunchHour = 0;
      tempGrouped[i].lunchMinute = 0;
    }
    for (let i = 0; i < tempIndividual.length; i++) {
      tempIndividual[i].lunch = 0;
      tempIndividual[i].lunchHour = 0;
      tempIndividual[i].lunchMinute = 0;
    }
    setTimesheetData(tempDefault);
    setTimesheetDataGrouped(tempGrouped);
    setTimesheetDataIndividual(tempIndividual);
  }, [selectedOption]);

  function updateLunchTime(id, hour, min) {
    let listToUse = timesheetData;
    if (selectedOption === t('grouped')) {
      listToUse = timesheetDataGrouped;
    } else if (selectedOption === t('individual')) {
      listToUse = timesheetDataIndividual;
    }
    listToUse = [...listToUse];
    for (let i = 0; i < listToUse.length; i++) {
      if (listToUse[i].id === id) {
        listToUse[i].lunchHour = hour;
        listToUse[i].lunchMinute = min;
        let tempMinutes = (min / 60);
        tempMinutes = tempMinutes.toPrecision(2);
        const tempSum = hour + parseFloat(tempMinutes);
        listToUse[i].lunch = tempSum;
      }
    }
    if (selectedOption === t('grouped')) {
      setTimesheetDataGrouped(listToUse);
    } else if (selectedOption === t('individual')) {
      setTimesheetDataIndividual(listToUse);
    } else {
      setTimesheetData(listToUse);
    }
  }

  const columns = [
    {
      title: t('day'),
      key: 'day',
      align: 'center',
      className: `text-xs ${selectedOption === t('default') ? '' : 'hidden'}`,
      render: (text) => <p>{renderShortDate(text.date, timezone)}</p>,
    },
    {
      title: t('start_time'),
      key: 'starttime',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => <p>{renderShortDateTime(text.startTime, timezone)}</p>,
    },
    {
      title: t('end_time'),
      key: 'endtime',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => (
        <div>
          {text.endTime && (<p>{renderShortDateTime(text.endTime, timezone)}</p>)}
          {!text.endTime && <p>-</p>}
        </div>
      ),
    },
    {
      title: t('first_name'),
      key: 'firstName',
      align: 'center',
      className: 'text-xs',
      render: (text) => <p>{text?.user?.firstName}</p>,
    },
    {
      title: t('last_name'),
      key: 'lastName',
      align: 'center',
      className: 'text-xs',
      render: (text) => <p>{text?.user?.lastName}</p>,
    },
    {
      title: t('hours'),
      key: 'totalhours',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? 'hidden' : ''}`,
      render: (text) => (
        <div>
          <Tooltip title={`${thousand(text.totalMinutes)} ${t('minutes')}`}>
            <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
            {thousandv2(text.totalHours)}
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('hours'),
      key: 'hours',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => (
        <div>
          {text.minutes && (
            <Tooltip title={`${thousand(text.minutes)} ${t('minutes')}`}>
              <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
              {thousandv2(text.hours)}
            </Tooltip>
          )}
          {!text.minutes && (
            <p>-</p>
          )}
        </div>
      ),
    },
    {
      title: t('type'),
      key: 'type',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => <p>{i18n.language === 'en' ? text?.type?.nameEn : text?.type?.nameEs}</p>,
    },
    {
      title: t('comments'),
      key: 'comments',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => <p className="text-left">{text.comment}</p>,
    },

    {
      title: t('lunch_time'),
      key: 'lunch',
      align: 'center',
      className: `w-64 text-xs ${showPayroll ? '' : 'hidden'}`,
      render: (text) => (
        <div className="flex space-x-2">
          <InputNumber
            addonAfter="hr"
            className="w-40"
            min={0}
            step="1"
            onChange={(obj) => updateLunchTime(text.id, obj, text.lunchMinute)}
            value={text.lunchHour}
          />
          <InputNumber
            addonAfter="min"
            className="w-40"
            min={0}
            step="1"
            onChange={(obj) => updateLunchTime(text.id, text.lunchHour, obj)}
            value={text.lunchMinute}
          />
        </div>
      ),
    },
    {
      title: t('gross_amount'),
      key: 'gross_amount',
      align: 'center',
      className: `text-xs ${showPayroll ? '' : 'hidden'}`,
      render: (text) => (
        <div>
          {toCurrency((selectedOption === t('individual') ? text.hours : text.totalHours) * hourlyRate)}
        </div>
      ),
    },
    {
      title: t('deduction'),
      key: 'deduction',
      align: 'center',
      className: `text-xs ${showPayroll ? '' : 'hidden'}`,
      render: (text) => (
        <div>
          {toCurrency((((selectedOption === t('individual') ? text.hours : text.totalHours) - text.lunch) * hourlyRate) * (deductionPercentage / 100))}
        </div>
      ),
    },
    {
      title: t('net'),
      key: 'net',
      align: 'center',
      className: `text-xs ${showPayroll ? '' : 'hidden'}`,
      render: (text) => (
        <div>
          {toCurrency((((selectedOption === t('individual') ? text.hours : text.totalHours) - text.lunch) * hourlyRate) - ((((selectedOption === t('individual') ? text.hours : text.totalHours) - text.lunch) * hourlyRate) * (deductionPercentage / 100)))}
        </div>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      className: `text-xs ${selectedOption === t('individual') ? '' : 'hidden'}`,
      render: (text) => (
        <Button
          type="primary"
          size="small"
          disabled={isStoreManager}
          onClick={() => {
            setSelectedTimeSheet({
              ...text,
              startTime: moment(text.startTime).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000'),
              endTime: text.endTime ? moment(text.endTime).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null
            });
            setShowModal(true);
          }}
        >
          {t('edit')}
        </Button>
      ),
    },
  ];

  function getTableSummary() {
    let minutes = 0;
    let hours = 0;
    let gross = 0;
    let deduction = 0;
    let net = 0;
    let lunchMinutes = 0;
    let lunch = 0;
    if (selectedOption === t('default')) {
      for (let i = 0; i < timesheetData.length; i++) {
        const tempData = timesheetData[i];
        minutes += tempData.totalMinutes;
        hours += tempData.totalHours;
        lunch += tempData.lunch;
        lunchMinutes += (tempData.lunchHour * 60) + tempData.lunchMinute;
        gross += (tempData.totalHours * hourlyRate) - tempData.lunch;
        deduction += ((tempData.totalHours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
        net += ((tempData.totalHours * hourlyRate) - tempData.lunch) -
        ((tempData.totalHours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
      }
      return (
        <Table.Summary.Row>
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold text-xs text-center">
            <div>
              <Tooltip title={`${thousand(minutes)} ${t('minutes')}`}>
                <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                {thousandv2(hours)}
              </Tooltip>
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">
            <div>
              <Tooltip title={`${thousand(lunchMinutes)} ${t('minutes')}`}>
                <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                {thousandv2(lunch)}
              </Tooltip>
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(gross)}</Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(deduction)}</Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(net)}</Table.Summary.Cell>
        </Table.Summary.Row>
      );
    }
    if (selectedOption === t('grouped')) {
      for (let i = 0; i < timesheetDataGrouped.length; i++) {
        const tempData = timesheetDataGrouped[i];
        minutes += tempData.totalMinutes;
        hours += tempData.totalHours;
        lunch += tempData.lunch;
        lunchMinutes += (tempData.lunchHour * 60) + tempData.lunchMinute;
        gross += (tempData.totalHours * hourlyRate) - tempData.lunch;
        deduction += ((tempData.totalHours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
        net += ((tempData.totalHours * hourlyRate) - tempData.lunch) -
        ((tempData.totalHours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
      }
      return (
        <Table.Summary.Row>
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold" />
          <Table.Summary.Cell className="font-bold text-xs text-center">
            <div>
              <Tooltip title={`${thousand(minutes)} ${t('minutes')}`}>
                <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                {thousandv2(hours)}
              </Tooltip>
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">
            <div>
              <Tooltip title={`${thousand(lunchMinutes)} ${t('minutes')}`}>
                <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                {thousandv2(lunch)}
              </Tooltip>
            </div>
          </Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(gross)}</Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(deduction)}</Table.Summary.Cell>
          <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(net)}</Table.Summary.Cell>
        </Table.Summary.Row>
      );
    }
    for (let i = 0; i < timesheetDataIndividual.length; i++) {
      const tempData = timesheetDataIndividual[i];
      minutes += tempData.minutes;
      hours += tempData.hours;
      lunch += tempData.lunch;
      lunchMinutes += (tempData.lunchHour * 60) + tempData.lunchMinute;
      gross += (tempData.hours * hourlyRate) - tempData.lunch;
      deduction += ((tempData.hours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
      net += ((tempData.hours * hourlyRate) - tempData.lunch) - ((tempData.hours * hourlyRate) - tempData.lunch) * (deductionPercentage / 100);
    }
    return (
      <Table.Summary.Row>
        <Table.Summary.Cell className="font-bold" />
        <Table.Summary.Cell className="font-bold" />
        <Table.Summary.Cell className="font-bold" />
        <Table.Summary.Cell className="font-bold" />
        <Table.Summary.Cell className="font-bold" />
        <Table.Summary.Cell className="font-bold text-xs text-center">
          <div>
            <Tooltip title={`${thousand(minutes)} ${t('minutes')}`}>
              <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
              {thousandv2(hours)}
            </Tooltip>
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell className="font-bold text-xs text-center">
          <div>
            <Tooltip title={`${thousand(lunchMinutes)} ${t('minutes')}`}>
              <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
              {thousandv2(lunch)}
            </Tooltip>
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(gross)}</Table.Summary.Cell>
        <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(deduction)}</Table.Summary.Cell>
        <Table.Summary.Cell className="font-bold text-xs text-center">{toCurrency(net)}</Table.Summary.Cell>
      </Table.Summary.Row>
    );
  }

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={t('timesheets')}
        tags={
          <Tag
            color="#2db7f5"
            icon={business ? <CheckCircleOutlined /> : <SyncOutlined spin />}
          >
            {business ? business.name : `${t('loading')}...`}
          </Tag>
        }
      />
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane tab={t('timesheets')} key="1">
          <div>
            <div className="flex space-x-2">
              <Button type="primary" size="small" className="ml-auto mt-2" disabled={!business} onClick={() => setCreateNewEntry(true)} loading={creatingNewEntry}>
                {t('create_new')}
              </Button>
              <ReactToPrint
                content={() => ref.current}
                trigger={() => (
                  <button
                    type="button"
                    className="flex items-center text-white text-xl justify-center bg-blue-600 hover:bg-blue-400 rounded-full w-10 h-10 ring-0 focus:ring-0 outline-none focus:outline-none transition duration-500 ease-in"
                  >
                    <PrinterOutlined className="h-5 w-5" />
                  </button>
                )}
              />
            </div>
            <div className="mt-2 grid md:flex">
              <div className="md:ml-auto mr-2">
                <RangePicker
                  disabled={loadingTimeSheet}
                  allowClear={false}
                  className="w-full"
                  format="YYYY-MM-DD"
                  value={[
                    dayjs(selectedDates?.start),
                    dayjs(selectedDates?.end)
                  ]}
                  presets={dateRangePresets(t)}
                  onChange={(date, dateString) => {
                    setSelectedDates({
                      ...selectedDates,
                      start: dateString[0],
                      end: dateString[1]
                    });
                  }}
                />
              </div>
              <div>
                <Select
                  listHeight={500}
                  disabled={loadingStaff || loadingTimeSheet}
                  className="w-52"
                  onChange={(obj) => setSelectedEmployeeId(obj)}
                  value={selectedEmployeeId}
                  showSearch="true"
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    if (option && option.children) {
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }
                    return false;
                  }}
                >
                  <Option value="">{t('any_employee')}</Option>
                  {_.map(staff, (s) => (
                    <Option value={s.userId}>
                      <span className={!s.isActive ? 'text-red-400' : ''}>{s.completeName}</span>
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  listHeight={500}
                  disabled={loadingTypes || loadingTimeSheet || types?.length <= 1}
                  className="ml-2 w-52"
                  onChange={(obj) => setSelectedType(obj)}
                  value={selectedType}
                  showSearch="true"
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    if (option && option.children) {
                      return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }
                    return false;
                  }}
                >
                  <Option value="">{t('any_type')}</Option>
                  {_.map(types, (s) => (
                    <Option value={s.id}>
                      {i18n.language === 'en' ? s.nameEn : s.nameEs}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="ml-2">
                <strong className="mr-1">{t('for_payroll')}</strong>
                <Switch
                  disabled={loadingTimeSheet}
                  checked={showPayroll}
                  onChange={(checked) => {
                    setShowPayroll(checked);
                  }}
                />
              </div>
              <div className="mt-1">
                <Button type="primary" size="small" className="ml-2" onClick={() => getTimeSheetData()} loading={loadingTimeSheet}>
                  {t('refresh')}
                </Button>
              </div>
            </div>
            <div className={`flex ${showPayroll ? '' : 'hidden'}`}>
              <div className="ml-auto">
                <p>{t('hourly_rate')}</p>
                <InputNumber
                  addonBefore="$"
                  min={0}
                  step="1"
                  onChange={(obj) => setHourlyRate(obj)}
                  value={hourlyRate}
                />
              </div>
              <div className="ml-2">
                <p>{t('deduction')}</p>
                <InputNumber
                  addonAfter="%"
                  min={0}
                  step="1"
                  onChange={(obj) => setDeductionPercentage(obj)}
                  value={deductionPercentage}
                />
              </div>
            </div>
            <Segmented
              size="medium"
              className="mt-2"
              block
              primary
              options={[t('default'), t('grouped'), t('individual')]}
              value={selectedOption}
              onChange={(value) => {
                setSelectedOption(value);
              }}
            />
            <div ref={ref}>
              <Table
                pagination={false}
                size="small"
                className="mt-2"
                bordered
                rowKey="id"
                loading={loadingTimeSheet}
                dataSource={selectedOption === t('default') ? timesheetData : (selectedOption === t('grouped') ? timesheetDataGrouped : timesheetDataIndividual)}
                columns={columns}
                expandable={{
                  expandRowByClick: false,
                  rowExpandable: (record) => selectedOption !== t('individual'),
                  expandedRowRender: (record) => (
                    <Table
                      pagination={false}
                      size="small"
                      bordered
                      rowKey="id"
                      dataSource={record.timesheet}
                      columns={[
                        {
                          title: t('start_time'),
                          key: 'starttime',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => <p>{renderShortDateTime(text.startTime, timezone)}</p>,
                        },
                        {
                          title: t('end_time'),
                          key: 'endtime',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => (
                            <div>
                              {text.endTime && (<p>{renderShortDateTime(text.endTime, timezone)}</p>)}
                              {!text.endTime && <p>-</p>}
                            </div>
                          ),
                        },
                        {
                          title: t('hours'),
                          key: 'hours',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => (
                            <div>
                              {text.minutes && (
                                <Tooltip title={`${thousand(text.minutes)} ${t('minutes')}`}>
                                  <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                                  {thousandv2(text.hours)}
                                </Tooltip>
                              )}
                              {!text.minutes && (
                                <p>-</p>
                              )}
                            </div>
                          ),
                        },
                        {
                          title: t('type'),
                          key: 'type',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => <p>{i18n.language === 'en' ? text.type.nameEn : text.type.nameEs}</p>,
                        },
                        {
                          title: t('comments'),
                          key: 'comments',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => <p className="text-left">{text.comment}</p>,
                        },
                        {
                          title: t('gross_amount'),
                          key: 'gross_amount',
                          align: 'center',
                          className: `text-xs ${showPayroll ? '' : 'hidden'}`,
                          render: (text) => (
                            <div>
                              {toCurrency(text.hours * hourlyRate)}
                            </div>
                          ),
                        },
                        {
                          title: t('deduction'),
                          key: 'deduction',
                          align: 'center',
                          className: `text-xs ${showPayroll ? '' : 'hidden'}`,
                          render: (text) => (
                            <div>
                              {toCurrency((text.hours * hourlyRate) * (deductionPercentage / 100))}
                            </div>
                          ),
                        },
                        {
                          title: t('net'),
                          key: 'net',
                          align: 'center',
                          className: `text-xs ${showPayroll ? '' : 'hidden'}`,
                          render: (text) => (
                            <div>
                              {toCurrency((text.hours * hourlyRate) - ((text.hours * hourlyRate) * (deductionPercentage / 100)))}
                            </div>
                          ),
                        },
                        {
                          title: t('actions'),
                          key: 'actions',
                          align: 'center',
                          className: 'text-xs',
                          render: (text) => (
                            <Button
                              type="primary"
                              size="small"
                              disabled={isStoreManager}
                              onClick={() => {
                                setSelectedTimeSheet({
                                  ...text,
                                  startTime: moment(text.startTime).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000'),
                                  endTime: text.endTime ? moment(text.endTime).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null
                                });
                                setShowModal(true);
                              }}
                            >
                              {t('edit')}
                            </Button>
                          ),
                        },
                      ]}
                    />
                  )
                }}
                summary={(pageData) => (showPayroll ? getTableSummary() : undefined)}
              />
            </div>
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('clocked_in')} key="2">
          <div className="flex flex-col">
            <div className="grid md:ml-auto md:flex md:space-x-2 space-y-2">
              <div className="grid md:flex space-y-2">
                <Button type="primary" size="small" className="ml-1" onClick={() => getClockedIn()} loading={loadingClockedIn}>
                  {t('refresh')}
                </Button>
              </div>
            </div>
            <Table
              pagination={false}
              size="small"
              bordered
              rowKey="id"
              loading={loadingClockedIn}
              dataSource={clockedInData}
              columns={[
                {
                  title: t('day'),
                  key: 'day',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => <p>{renderShortDateHour(text.startTime, timezone)}</p>,
                },
                {
                  title: t('first_name'),
                  key: 'firstName',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => <p>{text?.employee?.firstName}</p>,
                },
                {
                  title: t('last_name'),
                  key: 'lastName',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => <p>{text?.employee?.lastName}</p>,
                }
              ]}
            />
          </div>
        </Tabs.TabPane>
      </Tabs>
      <Modal
        title={t('edit_timesheet')}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={[
          <Popconfirm
            title={t('delete_timesheet_comfirmation_prompt')}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={() => {
              deleteTimesheet();
            }}
          >
            <Button
              key="delete"
              type="primary"
              className="mr-1"
              danger
              loading={updatingTimesheet}
            >
              {t('delete')}
            </Button>,
          </Popconfirm>,
          <Button
            key="close"
            type="primary"
            danger
            disabled={updatingTimesheet}
            onClick={() => setShowModal(false)}
          >
            {t('close')}
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={updatingTimesheet}
            onClick={() => updateTimesheet()}
          >
            {t('update')}
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <p>{t('start_time')}</p>
            <DatePicker
              showTime
              showHour
              showMinute
              allowClear={false}
              disabled={updatingTimesheet}
              className="w-full"
              format="YYYY-MM-DD hh:mm A"
              value={dayjs(selectedTimeSheet?.startTime)}
              onChange={(date, dateString) => {
                setSelectedTimeSheet({
                  ...selectedTimeSheet,
                  startTime: dayjs(dateString)
                });
              }}
            />
          </div>
          <div>
            <p>{t('end_time')}</p>
            <DatePicker
              showTime
              showHour
              showMinute
              allowClear={false}
              disabled={updatingTimesheet}
              className="w-full"
              format="YYYY-MM-DD hh:mm A"
              value={selectedTimeSheet?.endTime ? dayjs(selectedTimeSheet?.endTime) : null}
              onChange={(date, dateString) => {
                setSelectedTimeSheet({
                  ...selectedTimeSheet,
                  endTime: dayjs(dateString)
                });
              }}
            />
          </div>
          <div>
            <p>{t('type')}</p>
            <Select
              listHeight={500}
              disabled={updatingTimesheet || types?.length <= 1}
              className="w-full"
              onChange={(obj) => setSelectedTimeSheet({
                ...selectedTimeSheet,
                employeeTimeSheetTypeId: obj
              })}
              value={selectedTimeSheet?.employeeTimeSheetTypeId}
              showSearch="true"
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (option && option.children) {
                  return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              {_.map(types, (s) => (
                <Option value={s.id}>
                  {i18n.language === 'en' ? s.nameEn : s.nameEs}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <p>{t('comments')}</p>
            <Input
              disabled={creatingNewEntry}
              value={selectedTimeSheet?.comment}
              onChange={(obj) => setSelectedTimeSheet({
                ...selectedTimeSheet,
                comment: obj.target.value
              })}
            />
          </div>
          <div className="grid grid-cols-4 w-full">
            <div className={`mx-auto ${showPayroll ? 'col-span-1' : 'col-span-4'}`}>
              <p>{t('hours')}</p>
              {selectedTimeSheet?.startTime && selectedTimeSheet?.endTime && (
                <p className="text-center">{thousand(dayjs(selectedTimeSheet?.endTime).diff(dayjs(selectedTimeSheet?.startTime), 'minutes') / 60)}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('gross_amount')}</p>
              {selectedTimeSheet?.startTime && selectedTimeSheet?.endTime && (
                <p className="text-center">{toCurrency(dayjs(selectedTimeSheet?.endTime).diff(dayjs(selectedTimeSheet?.startTime), 'hours') * hourlyRate)}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('deduction')}</p>
              {selectedTimeSheet?.startTime && selectedTimeSheet?.endTime && (
                <p className="text-center">{toCurrency((dayjs(selectedTimeSheet?.endTime).diff(dayjs(selectedTimeSheet?.startTime), 'hours') * hourlyRate) * (deductionPercentage / 100))}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('net')}</p>
              {selectedTimeSheet?.startTime && selectedTimeSheet?.endTime && (
                <p className="text-center">{toCurrency((dayjs(selectedTimeSheet?.endTime).diff(dayjs(selectedTimeSheet?.startTime), 'hours') * hourlyRate) - ((dayjs(selectedTimeSheet?.endTime).diff(dayjs(selectedTimeSheet?.startTime), 'hours') * hourlyRate) * (deductionPercentage / 100)))}</p>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        title={t('create_new_timesheet')}
        open={createNewEntry}
        onCancel={() => setCreateNewEntry(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            danger
            disabled={creatingNewEntry}
            onClick={() => setCreateNewEntry(false)}
          >
            {t('close')}
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={creatingNewEntry || !newEntryData?.startTime || newEntryData?.userId === ''}
            loading={creatingNewEntry}
            onClick={() => createNewTimeSheetEntry()}
          >
            {t('create')}
          </Button>
        ]}
      >
        <div className="space-y-4">
          <div>
            <p>{t('employee')}</p>
            <Select
              listHeight={500}
              disabled={creatingNewEntry}
              className="w-full"
              onChange={(obj) => setNewEntryData({
                ...newEntryData,
                userId: obj
              })}
              value={newEntryData?.userId}
              showSearch="true"
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (option && option.children) {
                  return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              <Option disabled value="">{t('any')}</Option>
              {_.map(staff, (s) => (
                <Option value={s.userId}>{s.completeName}</Option>
              ))}
            </Select>
          </div>
          <div>
            <p>{t('start_time')}</p>
            <DatePicker
              showTime
              showHour
              showMinute
              disabled={creatingNewEntry}
              allowClear={false}
              className="w-full"
              format="YYYY-MM-DD hh:mm A"
              value={newEntryData?.startTime ? dayjs(newEntryData?.startTime) : null}
              onChange={(date, dateString) => {
                setNewEntryData({
                  ...newEntryData,
                  startTime: dayjs(dateString)
                });
              }}
            />
          </div>
          <div>
            <p>{t('end_time')}</p>
            <DatePicker
              showTime
              showHour
              showMinute
              allowClear={false}
              className="w-full"
              disabled={creatingNewEntry}
              format="YYYY-MM-DD hh:mm A"
              value={newEntryData?.endTime ? dayjs(newEntryData?.endTime) : null}
              onChange={(date, dateString) => {
                setNewEntryData({
                  ...newEntryData,
                  endTime: dayjs(dateString)
                });
              }}
            />
          </div>
          <div>
            <p>{t('type')}</p>
            <Select
              listHeight={500}
              disabled={creatingNewEntry || types?.length <= 1}
              className="w-full"
              onChange={(obj) => setNewEntryData({
                ...newEntryData,
                employeeTimeSheetTypeId: obj
              })}
              value={newEntryData?.employeeTimeSheetTypeId}
              showSearch="true"
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (option && option.children) {
                  return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }
                return false;
              }}
            >
              {_.map(types, (s) => (
                <Option value={s.id}>
                  {i18n.language === 'en' ? s.nameEn : s.nameEs}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <p>{t('comments')}</p>
            <Input
              disabled={creatingNewEntry}
              value={newEntryData?.comment}
              onChange={(obj) => setNewEntryData({
                ...newEntryData,
                comment: obj.target.value
              })}
            />
          </div>
          <div className="grid grid-cols-4 w-full">
            <div className={`mx-auto ${showPayroll ? 'col-span-1' : 'col-span-4'}`}>
              <p>{t('hours')}</p>
              {newEntryData?.startTime && newEntryData?.endTime && (
                <p className="text-center">{thousand(dayjs(newEntryData?.endTime).diff(dayjs(newEntryData?.startTime), 'minutes') / 60)}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('gross_amount')}</p>
              {newEntryData?.startTime && newEntryData?.endTime && (
                <p className="text-center">{toCurrency(dayjs(newEntryData?.endTime).diff(dayjs(newEntryData?.startTime), 'hours') * hourlyRate)}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('deduction')}</p>
              {newEntryData?.startTime && newEntryData?.endTime && (
                <p className="text-center">{toCurrency((dayjs(newEntryData?.endTime).diff(dayjs(newEntryData?.startTime), 'hours') * hourlyRate) * (deductionPercentage / 100))}</p>
              )}
            </div>
            <div className={`mx-auto ${showPayroll ? '' : 'hidden'}`}>
              <p>{t('net')}</p>
              {newEntryData?.startTime && newEntryData?.endTime && (
                <p className="text-center">{toCurrency((dayjs(newEntryData?.endTime).diff(dayjs(newEntryData?.startTime), 'hours') * hourlyRate) - ((dayjs(newEntryData?.endTime).diff(dayjs(newEntryData?.startTime), 'hours') * hourlyRate) * (deductionPercentage / 100)))}</p>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TeamTimesheets;
